import {
  type BusinessExecutionFrame,
  BusinessOperationExecutionUnconfirmedError,
  BUSINESS_OPERATION_EXECUTION_RECOVERY,
  consumeTextStream,
  DocumentExecutionRejectedError,
  DocumentExecutionUnconfirmedError,
  DOCUMENT_EXECUTION_REJECTED,
  DOCUMENT_EXECUTION_RECOVERY,
} from "@/lib/assistant/text-stream";
import {
  startSentryOperation,
  type SentryErrorCode,
  type SentryFailurePhase,
} from "@/integrations/sentry/operations";
import { closeAssistantConversation } from "../assistant-conversation-client";
import {
  AssistantThreadError,
  assistantThreadKeys,
  loadAssistantThreadPage,
  readCurrentThread,
  turnsFromMessages,
  writeCurrentThread,
} from "../assistant-threads";
import {
  type BusinessReceipt,
  type BusinessReconciliation,
  fetchRecentBusinessReceipts,
  mayReplaceReceipt,
  receiptFromFrame,
  settleUnfinished,
  withRecentDetail,
} from "../assistant-business-receipts";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getBackendClient } from "@/lib/backend";
import { ASSISTANT_TEXT_NOT_CONFIGURED } from "@/lib/assistant/model-settings";
import { currentOperatorClock } from "@/lib/assistant/operator-clock";
import type { VoiceTurn } from "@/lib/voice/conversation";

export const DIRECT_TEXT_UNAVAILABLE =
  "Text chat is unavailable. Your draft has been kept. Retry explicitly.";
export const DIRECT_TEXT_STREAM_INCOMPLETE =
  "Response incomplete and not retried. Check recent business commands and document receipts before retrying; do not create duplicates.";
export const DIRECT_TEXT_PROVIDER_UNAVAILABLE =
  "Text chat provider is unavailable. Your draft has been kept. Retry explicitly.";
export const DIRECT_TEXT_NOT_CONFIGURED =
  "Text chat is not configured. Your draft has been kept. An administrator must select a working provider in Settings.";
export const DIRECT_TEXT_SESSION_REQUIRED =
  "Your text chat session has expired. Your draft has been kept. Sign in again.";
export const DIRECT_TEXT_ACCESS_DENIED =
  "You do not have access to text chat. Your draft has been kept. Ask an administrator for access.";
export const DIRECT_TEXT_BACKEND_UPDATE_REQUIRED =
  "Assistant backend update required. Your draft has been kept. Retry after the update is deployed.";
const backend = getBackendClient();

export type DirectTextTurn = VoiceTurn & {
  /** Tab-local finalization ticket; consuming it survives transcript remounts. Never stored. */
  claimAutomaticRead?: () => boolean;
  messageEnvelope?: string;
  conversationId?: string;
  /** When the reply was requested; slow turns show their elapsed time. */
  startedAt?: number;
  receipts?: BusinessReceipt[];
  reconciliation?: BusinessReconciliation;
};

// Outcomes where a write may have committed without a receipt reaching the browser.
const RECONCILED_FAILURES = new Set([
  BUSINESS_OPERATION_EXECUTION_RECOVERY,
  DIRECT_TEXT_STREAM_INCOMPLETE,
  DOCUMENT_EXECUTION_RECOVERY,
]);

/** A turn that ended without an answer shows no progress and no in-flight command. */
export function settleTurn<T extends DirectTextTurn>(turn: T): T {
  if (turn.isFinal) return turn;
  return {
    ...turn,
    isIncomplete: true,
    startedAt: undefined,
    receipts: settleUnfinished(turn.receipts),
    ...(turn.reconciliation?.state === "loading"
      ? { reconciliation: { state: "unavailable" } }
      : {}),
  };
}

/**
 * One receipt per command. A later frame takes the earlier one's place only when
 * at least as definitive, so a refusal that does not prove the command did not
 * save never hides an earlier outcome that says it may have.
 */
function withReceipt(receipts: BusinessReceipt[] = [], receipt: BusinessReceipt) {
  const earlier = receipts.find((item) => item.commandId === receipt.commandId);
  if (!earlier) return [...receipts, receipt];
  return mayReplaceReceipt(receipt, earlier)
    ? receipts.map((item) => (item === earlier ? receipt : item))
    : receipts;
}

function statusCodeOf(error: unknown): number | null {
  if (typeof error !== "object" || error === null) return null;
  const record = error as { status?: unknown; statusCode?: unknown };
  return typeof record.statusCode === "number"
    ? record.statusCode
    : typeof record.status === "number"
      ? record.status
      : null;
}

const SAFE_DIRECT_TEXT_FAILURES = new Set([
  BUSINESS_OPERATION_EXECUTION_RECOVERY,
  DOCUMENT_EXECUTION_REJECTED,
  DOCUMENT_EXECUTION_RECOVERY,
  DIRECT_TEXT_ACCESS_DENIED,
  DIRECT_TEXT_BACKEND_UPDATE_REQUIRED,
  DIRECT_TEXT_NOT_CONFIGURED,
  DIRECT_TEXT_PROVIDER_UNAVAILABLE,
  DIRECT_TEXT_SESSION_REQUIRED,
  DIRECT_TEXT_UNAVAILABLE,
]);

/** The server's refusal text: the adapter's `code`/`message`, or a raw JSON body's `error`. */
function refusalOf(error: unknown): unknown[] {
  if (typeof error !== "object" || error === null) return [];
  const record = error as {
    code?: unknown;
    error?: unknown;
    message?: unknown;
  };
  return [record.code, record.error, record.message];
}

function messageForDirectTextStatus(statusCode: number | null, refusal: unknown[] = []): string {
  // Misconfiguration needs an administrator; any other 503 is a provider outage to retry.
  if (statusCode === 503 && refusal.includes(ASSISTANT_TEXT_NOT_CONFIGURED))
    return DIRECT_TEXT_NOT_CONFIGURED;
  switch (statusCode) {
    case 401:
      return DIRECT_TEXT_SESSION_REQUIRED;
    case 403:
      return DIRECT_TEXT_ACCESS_DENIED;
    case 404:
      return DIRECT_TEXT_BACKEND_UPDATE_REQUIRED;
    case 503:
      return DIRECT_TEXT_PROVIDER_UNAVAILABLE;
    default:
      return DIRECT_TEXT_UNAVAILABLE;
  }
}

function messageForDirectTextFailure(error: unknown): string {
  return messageForDirectTextStatus(statusCodeOf(error), refusalOf(error));
}

async function messageForDirectTextResponse(response: Response): Promise<string> {
  const body: unknown = response.status === 503 ? await response.json().catch(() => null) : null;
  return messageForDirectTextStatus(response.status, refusalOf(body));
}

function directTextFailureMessage(error: unknown): string {
  return error instanceof Error && SAFE_DIRECT_TEXT_FAILURES.has(error.message)
    ? error.message
    : DIRECT_TEXT_UNAVAILABLE;
}

function streamFailureMessage(error: unknown, phase: SentryFailurePhase): string {
  const message = directTextFailureMessage(error);
  return phase === "stream" && message === DIRECT_TEXT_UNAVAILABLE
    ? DIRECT_TEXT_STREAM_INCOMPLETE
    : message;
}

function incompleteTurnText(text: string, error: unknown): string {
  if (error instanceof BusinessOperationExecutionUnconfirmedError)
    return BUSINESS_OPERATION_EXECUTION_RECOVERY;
  if (error instanceof DocumentExecutionRejectedError) return DOCUMENT_EXECUTION_REJECTED;
  if (error instanceof DocumentExecutionUnconfirmedError) return DOCUMENT_EXECUTION_RECOVERY;
  return `${text === "Thinking…" ? "" : text + "\n\n"}Response incomplete. Check recent business commands and document receipts before retrying.`;
}

function directTextFailureForSentry(
  error: unknown,
  failurePhase: SentryFailurePhase,
  cancelled: boolean,
): {
  errorCode: SentryErrorCode;
  expected?: true;
  failurePhase: SentryFailurePhase;
} {
  const aborted = error instanceof DOMException && error.name === "AbortError";
  if (cancelled || aborted) {
    return { errorCode: "cancelled", expected: true, failurePhase };
  }

  switch (directTextFailureMessage(error)) {
    case DIRECT_TEXT_ACCESS_DENIED:
      return { errorCode: "access_denied", expected: true, failurePhase };
    case DIRECT_TEXT_SESSION_REQUIRED:
      return { errorCode: "session_required", expected: true, failurePhase };
    case DIRECT_TEXT_BACKEND_UPDATE_REQUIRED:
      return { errorCode: "backend_update_required", failurePhase };
    case DIRECT_TEXT_NOT_CONFIGURED:
      return { errorCode: "not_configured", failurePhase };
    case DIRECT_TEXT_PROVIDER_UNAVAILABLE:
      return { errorCode: "provider_unavailable", failurePhase };
    default:
      return { errorCode: "stream_incomplete", failurePhase };
  }
}

function reportDirectTextCancellation(
  reportFailure: (error: unknown, failure: ReturnType<typeof directTextFailureForSentry>) => void,
  failurePhase: SentryFailurePhase,
): void {
  reportFailure(new DOMException("Text request cancelled", "AbortError"), {
    errorCode: "cancelled",
    expected: true,
    failurePhase,
  });
}

/**
 * A successful open whose answer names no usable text thread. It carries the
 * shared failure message, but its type tells it apart from a timeout or network
 * failure, which keep a resumed thread for the retry.
 */
class InvalidOpenedThread extends Error {
  constructor() {
    super(DIRECT_TEXT_UNAVAILABLE);
    this.name = "InvalidOpenedThread";
  }
}

/** The opened conversation and its thread, or the shared failure when the server's answer is not a text conversation in a thread. */
function openedThread(data: Record<string, unknown>): { conversationId: string; threadId: string } {
  const { mode, conversationId, threadId } = data;
  if (mode !== "text" || typeof conversationId !== "string" || typeof threadId !== "string")
    throw new InvalidOpenedThread();
  return { conversationId, threadId };
}

function completedTextAnswer(
  data: { conversationId?: unknown; isFinal?: unknown; text?: unknown },
  conversationId: string | null,
): string {
  const answer = typeof data.text === "string" ? data.text : "";
  if (!answer.trim()) throw new Error(DIRECT_TEXT_UNAVAILABLE);
  if (data.conversationId !== conversationId || data.isFinal !== true)
    throw new Error(DIRECT_TEXT_UNAVAILABLE);
  return answer;
}

/** Provider Settings is useful only for an explicitly identified provider failure. */
export function canChangeDirectTextProvider(message?: string): boolean {
  return (
    message === DIRECT_TEXT_NOT_CONFIGURED ||
    message === DIRECT_TEXT_PROVIDER_UNAVAILABLE ||
    message === DIRECT_TEXT_UNAVAILABLE
  );
}

function withDeadline<T>(pending: Promise<T>, milliseconds: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(DIRECT_TEXT_UNAVAILABLE)), milliseconds);
    pending.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * One operator's text chat. `actorId` scopes the remembered current thread, so
 * a reload resumes it and another operator on this browser never sees it.
 */
export function useDirectText(actorId: string | null = null) {
  const queryClient = useQueryClient();
  const [turns, setTurns] = useState<DirectTextTurn[]>([]);
  const [pending, setPending] = useState(false);
  const [errorMessage, setError] = useState<string>();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(() => readCurrentThread(actorId));
  const generation = useRef(0);
  const reference = useRef<string | null>(null);
  const opening = useRef<Promise<string | null> | null>(null);
  const pendingRevocation = useRef<string | null>(null);
  const closing = useRef(false);
  const busy = useRef(false);
  const activeRequest = useRef<AbortController | null>(null);
  // The thread outlives each conversation: a new conversation in this chat
  // resumes it, and the server supplies the model's prior context from it.
  const thread = useRef<string | null>(threadId);
  const mounted = useRef(true);
  const forgetThread = () => {
    thread.current = null;
    if (mounted.current) setThreadId(null);
  };
  const mutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await backend.functions.invoke<Record<string, unknown>>(
        "assistant-text",
        { body, retry: false },
      );
      if (error || !data) {
        // The status rides along so a caller can tell a refusal from an outage.
        throw Object.assign(new Error(messageForDirectTextFailure(error)), {
          status: statusCodeOf(error),
        });
      }
      return data;
    },
    retry: false,
    gcTime: 0,
  });
  const invoke = mutation.mutateAsync;
  const streamMutation = useMutation({
    mutationFn: async ({
      body,
      signal,
      conversationId,
      onText,
      onExecution,
    }: {
      body: Record<string, unknown>;
      signal: AbortSignal;
      conversationId: string;
      onText: (text: string) => void;
      onExecution: (frame: BusinessExecutionFrame) => void;
    }) => {
      const response = await backend.functions.invokeRaw("assistant-text", {
        body,
        signal,
        accept: "text/event-stream",
      });
      if (!response.ok) throw new Error(await messageForDirectTextResponse(response));
      return consumeTextStream(response, signal, conversationId, onText, onExecution);
    },
    retry: false,
    gcTime: 0,
  });
  const updateTurn = useCallback(
    (id: string, update: (turn: DirectTextTurn) => DirectTextTurn) =>
      setTurns((previous) => previous.map((turn) => (turn.id === id ? update(turn) : turn))),
    [],
  );
  // Recovery reads the owner's recent commands; it never retries the turn.
  const reconcile = useCallback(
    async (id: string, conversationId: string | null, showRecent: boolean) => {
      const started = generation.current;
      if (showRecent)
        updateTurn(id, (turn) => ({
          ...turn,
          reconciliation: { state: "loading" },
        }));
      const recent = await fetchRecentBusinessReceipts(conversationId);
      if (!mounted.current || started !== generation.current) return;
      updateTurn(id, (turn) => ({
        ...turn,
        ...(recent && turn.receipts ? { receipts: withRecentDetail(turn.receipts, recent) } : {}),
        ...(showRecent
          ? {
              reconciliation: recent
                ? { state: "loaded", receipts: recent }
                : { state: "unavailable" },
            }
          : {}),
      }));
    },
    [updateTurn],
  );

  const close = useCallback(async () => {
    closing.current = true;
    generation.current++;
    activeRequest.current?.abort();
    activeRequest.current = null;
    busy.current = false;
    setPending(false);
    setTurns((current) =>
      current.map((turn) =>
        !turn.isFinal
          ? {
              ...settleTurn(turn),
              text: `${turn.text === "Thinking…" ? "" : turn.text + "\n\n"}Response interrupted. Check recent business commands and document receipts before retrying; do not create duplicates.`,
            }
          : turn,
      ),
    );
    // A mint may finish after cancellation. Await it and revoke before allowing another mode.
    let id = pendingRevocation.current ?? reference.current;
    if (!id && opening.current) {
      try {
        id = await withDeadline(opening.current, 15_000);
      } catch {
        if (!opening.current && !pendingRevocation.current) closing.current = false;
        throw new Error("The previous conversation could not be closed. Please retry.");
      }
    }
    if (id) {
      pendingRevocation.current = id;
      await closeAssistantConversation(id);
      pendingRevocation.current = null;
    }
    reference.current = null;
    if (mounted.current) setConversationId(null);
    opening.current = null;
    closing.current = false;
  }, []);

  const sendText = useCallback(
    async (text: string): Promise<boolean> => {
      const content = text.trim();
      if (!content || busy.current || closing.current || pendingRevocation.current) return false;
      busy.current = true;
      setPending(true);
      setError(undefined);
      const current = generation.current;
      const id = crypto.randomUUID();
      return startSentryOperation(
        {
          operation: "assistant.text.request",
          provider: "insforge",
          requestId: id,
        },
        async ({ reportFailure }) => {
          let failurePhase: SentryFailurePhase = "request";
          let turnConversation: string | null = null;
          let turnReceipts: BusinessReceipt[] = [];
          try {
            if (!reference.current) {
              const openGeneration = generation.current;
              const resuming = thread.current;
              opening.current ??= invoke({ action: "open", threadId: resuming ?? undefined })
                .then((data) => {
                  const opened = openedThread(data);
                  thread.current = opened.threadId;
                  setThreadId(opened.threadId);
                  const openedId = opened.conversationId;
                  if (openGeneration !== generation.current || !mounted.current) {
                    pendingRevocation.current = openedId;
                    return closeAssistantConversation(openedId).then(() => {
                      pendingRevocation.current = null;
                      return null;
                    });
                  }
                  reference.current = openedId;
                  setConversationId(openedId);
                  return openedId;
                })
                .catch((error: unknown) => {
                  opening.current = null;
                  // A thread the server refuses (400 malformed; 403 gone or not
                  // this operator's) or answers with no usable thread is
                  // forgotten, so the next send starts a new one instead of
                  // failing the same way until a reload. An outage, timeout, or
                  // network failure keeps it for the retry.
                  const refused =
                    error instanceof InvalidOpenedThread ||
                    [400, 403].includes(statusCodeOf(error) ?? 0);
                  if (refused && resuming && thread.current === resuming) forgetThread();
                  throw error;
                });
              await withDeadline(opening.current, 15_000);
              opening.current = null;
            }
            if (!mounted.current || current !== generation.current) {
              reportDirectTextCancellation(reportFailure, failurePhase);
              return false;
            }
            setTurns((previous) => [
              ...previous,
              {
                id: `${id}-user`,
                isFinal: true,
                role: "operator",
                text: content,
              },
              {
                id,
                isFinal: false,
                role: "assistant",
                text: "Thinking…",
                conversationId: reference.current ?? undefined,
                startedAt: Date.now(),
              },
            ]);
            const request = new AbortController();
            activeRequest.current = request;
            failurePhase = "stream";
            turnConversation = reference.current;
            const signal = AbortSignal.any([request.signal, AbortSignal.timeout(70_000)]);
            const data = await streamMutation.mutateAsync({
              body: {
                action: "turn",
                conversationId: reference.current,
                text: content,
                operatorClock: currentOperatorClock() ?? undefined,
              },
              signal,
              conversationId: reference.current!,
              onExecution: (frame) => {
                const receipt = receiptFromFrame(frame);
                turnReceipts = withReceipt(turnReceipts, receipt);
                if (!mounted.current || current !== generation.current) return;
                updateTurn(id, (turn) => ({
                  ...turn,
                  receipts: withReceipt(turn.receipts, receipt),
                }));
              },
              onText: (text: string) => {
                if (!mounted.current || current !== generation.current || signal.aborted) return;
                setTurns((previous) =>
                  previous.map((turn) =>
                    turn.id === id ? { ...turn, text: text || "Thinking…" } : turn,
                  ),
                );
              },
            });
            if (!mounted.current || current !== generation.current) {
              reportDirectTextCancellation(reportFailure, failurePhase);
              return false;
            }
            const answer = completedTextAnswer(data, reference.current);
            let automaticReadClaimed = false;
            const claimAutomaticRead = () => {
              if (automaticReadClaimed) return false;
              automaticReadClaimed = true;
              return true;
            };
            setTurns((previous) =>
              previous.map((turn) =>
                turn.id === id
                  ? {
                      ...turn,
                      text: answer,
                      isFinal: true,
                      claimAutomaticRead,
                      conversationId: reference.current ?? undefined,
                      ...(typeof data.messageEnvelope === "string"
                        ? { messageEnvelope: data.messageEnvelope }
                        : {}),
                    }
                  : turn,
              ),
            );
            // Frames name no operation or reason for a command that did not complete.
            if (turnReceipts.some((receipt) => receipt.status !== "created"))
              void reconcile(id, turnConversation, false);
            return true;
          } catch (error) {
            reportFailure(
              error,
              directTextFailureForSentry(
                error,
                failurePhase,
                current !== generation.current || !mounted.current,
              ),
            );
            if (mounted.current && current === generation.current) {
              const failure = streamFailureMessage(error, failurePhase);
              setError(failure);
              setTurns((previous) =>
                previous.map((turn) =>
                  turn.id === id
                    ? {
                        ...settleTurn(turn),
                        text: incompleteTurnText(turn.text, error),
                      }
                    : turn,
                ),
              );
              if (failurePhase === "stream" && RECONCILED_FAILURES.has(failure))
                void reconcile(id, turnConversation, true);
            }
            return false;
          } finally {
            if (current === generation.current) {
              activeRequest.current = null;
              busy.current = false;
              if (mounted.current) setPending(false);
            }
            mutation.reset();
            streamMutation.reset();
            // The turn's messages are stored server-side; refresh any view of them.
            void queryClient.invalidateQueries({ queryKey: assistantThreadKeys.all(actorId) });
          }
        },
      );
    },
    [actorId, invoke, mutation, queryClient, reconcile, streamMutation, updateTurn],
  );

  // A reload's restore stands until the transcript is cleared or the thread
  // switches; either supersedes its late page.
  const restorePending = useRef(false);
  /**
   * Ends the open conversation, then shows `next` (or a fresh thread) with its
   * stored turns. Refused while a reply is in flight: switching would cancel it,
   * and the caller may hold a render from before the send began.
   */
  const switchThread = useCallback(
    async (next: string | null, nextTurns: DirectTextTurn[]) => {
      if (busy.current) return false;
      restorePending.current = false;
      await close();
      thread.current = next;
      if (!mounted.current) return true;
      setThreadId(next);
      setTurns(nextTurns);
      setError(undefined);
      return true;
    },
    [close],
  );
  const newThread = useCallback(() => switchThread(null, []), [switchThread]);
  /**
   * Makes `next` the current thread without ending anything or changing the
   * transcript: a voice call opened it, so the next typed message continues it.
   * Null forgets a thread the server refused.
   */
  const adoptThread = useCallback((next: string | null) => {
    thread.current = next;
    if (mounted.current) setThreadId(next);
  }, []);

  useEffect(() => writeCurrentThread(actorId, threadId), [actorId, threadId]);

  // The remembered thread's stored messages fill an empty transcript; a thread
  // the server refuses (malformed, gone, or not this operator's) is forgotten,
  // while an outage keeps it for the next send.
  useEffect(() => {
    const restored = thread.current;
    restorePending.current = Boolean(restored);
    if (!restored) return;
    // Applied while the same thread is still selected and nothing has filled the
    // transcript: a close in between (a live call starting) must not lose it.
    loadAssistantThreadPage(queryClient, actorId, restored).then(
      (page) => {
        if (!mounted.current || !restorePending.current || thread.current !== restored) return;
        restorePending.current = false;
        // A message sent before the page arrived stays after the history; its own
        // conversation's stored copies are already on screen.
        setTurns((current) => {
          const shown = new Set(current.map((turn) => turn.conversationId).filter(Boolean));
          const history = page.messages.filter(
            (message) => !message.conversationId || !shown.has(message.conversationId),
          );
          return [...turnsFromMessages(history), ...current];
        });
      },
      (error: unknown) => {
        if (
          error instanceof AssistantThreadError &&
          [400, 403].includes(error.status ?? 0) &&
          thread.current === restored
        )
          forgetThread();
      },
    );
  }, [actorId, queryClient]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      void close().catch(() => undefined);
    };
  }, [close]);

  return {
    close,
    errorMessage,
    pending,
    sendText,
    turns,
    conversationId,
    threadId,
    resumeThread: switchThread,
    newThread,
    forgetThread,
    adoptThread,
    /** True from the moment a send starts until its reply settles; read synchronously. */
    isReplying: () => busy.current,
    clear: () => {
      restorePending.current = false;
      setTurns([]);
    },
  };
}
